import { useEffect, useState } from "react";
import { safeHtml } from "@/lib/sanitize";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Truck, AlertCircle, Clock, Mail, Phone, MapPin } from "lucide-react";
import { getPublicSiteInfo } from "@/lib/api";
import SectionRenderer from "@/components/SectionRenderer";
import SiteBroadcastBanners from "@/components/SiteBroadcastBanners";
import { useTenantBranding, TenantLogo } from "@/components/TenantBrand";

/**
 * Public site-info viewer linked from booking emails.
 * Route: /p/:clientSlug/site-info/:siteId  (anonymous, RLS-safe)
 */
export default function PublicSiteInfo() {
  const { clientSlug, siteId } = useParams();
  const [state, setState] = useState({
    loading: true,
    site: null,
    rules: null,
    sections: [],
    err: null,
  });
  const brand = useTenantBranding({ site_id: siteId });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getPublicSiteInfo(clientSlug, siteId);
        if (cancelled) return;
        setState({
          loading: false,
          site: res.site,
          rules: res.rules,
          sections: res.sections || [],
          err: null,
        });
      } catch (e) {
        if (cancelled) return;
        setState((s) => ({ ...s, loading: false, err: e.message }));
      }
    })();
    return () => { cancelled = true; };
  }, [clientSlug, siteId]);

  const { loading, site, rules, sections, err } = state;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="public-site-info">
      <div className="max-w-3xl mx-auto">
        <TenantLogo brand={brand} className="mb-4" />
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
          <Truck className="h-4 w-4 text-blue-600" />
          <span className="font-semibold text-slate-900">timeslot.au</span>
          <span>·</span>
          <span>Site information &amp; requirements</span>
        </div>

        {loading && (
          <Card><CardContent className="py-10 text-center text-slate-500">Loading site information…</CardContent></Card>
        )}

        {!loading && err && (
          <Card><CardContent className="py-10 text-center text-rose-600 flex items-center justify-center gap-2">
            <AlertCircle className="h-4 w-4" /> {err}
          </CardContent></Card>
        )}

        {!loading && !err && site && (
          <div className="space-y-4">
            <Card>
              <CardContent className="py-6 space-y-4">
                <div>
                  <h1 className="text-2xl font-semibold tracking-tight">{site.name}</h1>
                  <div className="text-sm text-slate-500 mt-1 flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" /> {[site.city, site.state].filter(Boolean).join(", ")}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {(site.operating_hours_start || site.operating_hours_end) && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <Clock className="h-4 w-4 text-slate-400" />
                      <span>
                        Operating hours:{" "}
                        <strong>
                          {site.operating_hours_start || "—"} – {site.operating_hours_end || "—"}
                        </strong>{" "}
                        ({site.timezone || "AEST"})
                      </span>
                    </div>
                  )}
                  {site.contact_email && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <Mail className="h-4 w-4 text-slate-400" />
                      <a href={`mailto:${site.contact_email}`} className="hover:underline">{site.contact_email}</a>
                    </div>
                  )}
                  {site.contact_phone && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <Phone className="h-4 w-4 text-slate-400" />
                      <a href={`tel:${site.contact_phone}`} className="hover:underline">{site.contact_phone}</a>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Broadcast banners (between Site Name card and Rules card) */}
            <SiteBroadcastBanners siteId={site.id} />

            {/* Site rules sections (reuses existing renderer) */}
            {sections.length > 0 && (
              <Card>
                <CardContent className="py-6 space-y-6">
                  <h2 className="text-lg font-semibold">Site rules &amp; requirements</h2>
                  {sections.map((sec) => (
                    <SectionRenderer key={sec.id} section={sec} />
                  ))}
                </CardContent>
              </Card>
            )}

            {sections.length === 0 && rules?.body_html && (
              <Card>
                <CardContent className="py-6 space-y-3 prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={safeHtml(rules.body_html)}
                />
              </Card>
            )}

            {sections.length === 0 && !rules?.body_html && (
              <Card>
                <CardContent className="py-6 text-sm text-slate-500">
                  No site-specific rules have been published yet. Please contact the site directly using the details above.
                </CardContent>
              </Card>
            )}
          </div>
        )}

        <p className="text-center text-xs text-slate-400 mt-6">Powered by timeslot.au</p>
      </div>
    </div>
  );
}
